import * as React from 'react';
import propz from 'propz';
import classNames from 'classnames';
import { Component } from 'react';
import {
  ColumnDefinition,
  CustomCellColumnDefinition,
  FunctionAccessorColumnDefinition,
  isCustomCellColumnDefinition,
  isFunctionAccessorBasedColumnDefinition,
  isPathAccessorBasedColumnDefinition,
  PathAccessorColumnDefinition
} from '../../helpers/table/table';
import { TextCell } from './Cells/TextCell';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from '../../models/tournament';
import { ListEmptyState } from '../ListEmptyState/ListEmptyState';
import { faInbox } from '@fortawesome/free-solid-svg-icons';

interface Props {
  dataItems: any[];
  dataItemsSelected?: any[];
  columns: ColumnDefinition[];
  onDataItemClick: (index: number) => void;
  onDataItemSelect?: (index: number) => void;
  user?: AppUser;
  tournament?: Tournament;
  isSelectDisabled: boolean;
  isSelectionHidden?: boolean;
  isDataItemSelectionDisabled?: (item: any) => boolean;
  stylizeCell: (cell: any) => string;
  onCellIconClick?: (data, cell) => void;
  emptyText?: string;
  emptyHint?: string;
  emptyFilteredText?: string;
  emptyFilteredHint?: string;
  emptyIcon?: any;
  isDataFiltered?: boolean;
}

export class GridBody extends Component<Props> {
  constructor(props) {
    super(props);
  }

  static defaultProps = {
    dataItemsSelected: []
  };

  render() {
    const {
      dataItems,
      columns,
      dataItemsSelected,
      onDataItemClick,
      onDataItemSelect,
      user,
      tournament,
      isSelectDisabled,
      isSelectionHidden,
      stylizeCell,
      onCellIconClick,
      emptyText,
      emptyHint,
      emptyFilteredText,
      emptyFilteredHint,
      emptyIcon,
      isDataFiltered
    } = this.props;
    const options = { user, tournament };
    const handleDataItemSelect = onDataItemSelect || onDataItemClick;

    const renderDataRow = (dataItem: any, columns: ColumnDefinition[]) => {
      const rowCells = columns.map(column => {
        switch (true) {
          case isPathAccessorBasedColumnDefinition(column): {
            const pathAccessorBasedColumn = column as PathAccessorColumnDefinition;
            const value = propz.get<any>(dataItem, pathAccessorBasedColumn.accessor, '');
            const strValue = String(value);

            let cellClass = '';
            if (stylizeCell) {
              cellClass = stylizeCell({
                rowData: dataItem,
                column,
                value: strValue
              });
            }

            return <TextCell key={column.field} text={strValue} customClass={cellClass} />;
          }

          case isFunctionAccessorBasedColumnDefinition(column): {
            const funcAccessorColumn = column as FunctionAccessorColumnDefinition;
            const value = funcAccessorColumn.accessor(dataItem, options);
            const strValue = String(value);

            let cellClass = '';
            if (stylizeCell) {
              cellClass = stylizeCell({
                rowData: dataItem,
                column,
                value: strValue
              });
            }

            return <TextCell key={column.field} text={strValue} customClass={cellClass} />;
          }

          case isCustomCellColumnDefinition(column): {
            const cellColumn = column as CustomCellColumnDefinition;
            const cell = cellColumn.cell(dataItem, options, onCellIconClick);
            return cell;
          }

          default: {
            console.error('Not supported column type: ' + JSON.stringify(column, null, 2));
            return null;
          }
        }
      });

      return rowCells;
    };

    return (
      <tbody>
        {dataItems.length === 0 && emptyText ? (
          <tr className="gridModernEmptyRow">
            <td colSpan={columns.length + (isSelectionHidden ? 0 : 1)}>
              <ListEmptyState
                icon={emptyIcon || faInbox}
                title={isDataFiltered ? emptyFilteredText || 'No records match your filters' : emptyText}
                description={
                  isDataFiltered
                    ? emptyFilteredHint || 'Try changing or clearing the search and filters to see more records.'
                    : emptyHint || 'New records will appear here.'
                }
              />
            </td>
          </tr>
        ) : null}
        {dataItems.map((dataItem, index) => {
          const isDataItemSelected = dataItemsSelected.some(dataItemSelected => dataItemSelected.id === dataItem.id);
          const selectionDisabled = isSelectDisabled || Boolean(this.props.isDataItemSelectionDisabled?.(dataItem));

          const rowClasses = classNames({
            'table-primary': isDataItemSelected
          });

          return (
            <tr
              key={typeof dataItem.id === 'string' ? dataItem.id : `row_${index}`}
              className={rowClasses}
              onClick={() => onDataItemClick(index)}
            >
              {!isSelectionHidden ? (
                <td>
                  <div className="custom-checkbox">
                    <input
                      type="checkbox"
                      onClick={e => e.stopPropagation()}
                      onChange={() => {
                        if (!selectionDisabled) {
                          handleDataItemSelect(index);
                        }
                      }}
                      checked={isDataItemSelected}
                      disabled={selectionDisabled}
                    />
                  </div>
                </td>
              ) : null}
              {React.Children.map(renderDataRow(dataItem, columns), (child, cellIndex) =>
                React.isValidElement(child)
                  ? React.cloneElement(child, {
                      key: `row-${dataItem.id || index}-cell-${cellIndex}`
                    })
                  : child
              )}
            </tr>
          );
        })}
      </tbody>
    );
  }
}


